/**
 * Parses a rendered article HTML string (the remark-html output stored in
 * `magazineContent` / `blogContent`) and extracts an FAQ section into a
 * schema.org FAQPage object, ready to be serialised into a JSON-LD script.
 *
 * The expected shape, authored in the CMS markdown, is an `<h2>FAQ</h2>`
 * heading followed by one `<h3>` per question, each answered by the markup
 * that follows it up to the next question (or the next `<h2>` / end of the
 * FAQ section). Returns `null` when no FAQ section or no questions are found,
 * so callers can conditionally render the script tag.
 */

const NAMED_ENTITIES: Record<string, string> = {
  '&nbsp;': ' ',
  '&quot;': '"',
  '&apos;': "'",
  '&hellip;': '…',
  '&euro;': '€',
  '&pound;': '£',
  '&ndash;': '–',
  '&mdash;': '—',
  '&lsquo;': '‘',
  '&rsquo;': '’',
  '&ldquo;': '“',
  '&rdquo;': '”',
  '&copy;': '©',
  '&reg;': '®',
  '&trade;': '™',
  '&lt;': '<',
  '&gt;': '>',
};

const decodeEntities = (input: string): string =>
  input
    .replace(/&[a-z]+;/gi, (entity) => NAMED_ENTITIES[entity.toLowerCase()] ?? entity)
    .replace(/&#0?39;|&#x27;/gi, "'")
    .replace(/&#(\d+);/g, (_, dec: string) => String.fromCodePoint(Number(dec)))
    .replace(/&#x([0-9a-fA-F]+);/g, (_, hex: string) => String.fromCodePoint(parseInt(hex, 16)))
    // decode &amp; last so the escaped ampersand of another entity is not double-decoded
    .replace(/&amp;/g, '&');

const stripTags = (input: string): string => input.replace(/<[^>]*>/g, ' ');

const clean = (input: string): string =>
  decodeEntities(stripTags(input))
    .replace(/\s+/g, ' ')
    // drop the stray space a stripped inline tag can leave before punctuation
    .replace(/\s+([.,;:!?])/g, '$1')
    .trim();

const isFaqHeading = (headingText: string): boolean => {
  const normalized = clean(headingText).toUpperCase();
  return normalized.startsWith('FAQ') || normalized.includes('FREQUENTLY ASKED');
};

export interface FaqEntry {
  question: string;
  answer: string;
}

export const extractFaqEntries = (html: string): FaqEntry[] => {
  if (!html) return [];

  // Locate the FAQ <h2> and the start of the next <h2>, which bounds the section.
  const h2Regex = /<h2\b[^>]*>([\s\S]*?)<\/h2>/gi;
  let faqSectionStart = -1;
  let faqSectionEnd = html.length;
  let h2Match: RegExpExecArray | null;
  while ((h2Match = h2Regex.exec(html)) !== null) {
    if (faqSectionStart === -1) {
      if (isFaqHeading(h2Match[1])) {
        faqSectionStart = h2Regex.lastIndex;
      }
    } else {
      faqSectionEnd = h2Match.index;
      break;
    }
  }

  if (faqSectionStart === -1) return [];

  const section = html.slice(faqSectionStart, faqSectionEnd);

  // Each <h3> is a question; its answer is everything up to the next <h3>.
  const h3Regex = /<h3\b[^>]*>([\s\S]*?)<\/h3>/gi;
  const questions: { text: string; questionStart: number; answerStart: number }[] = [];
  let h3Match: RegExpExecArray | null;
  while ((h3Match = h3Regex.exec(section)) !== null) {
    questions.push({
      text: clean(h3Match[1]),
      questionStart: h3Match.index,
      answerStart: h3Regex.lastIndex,
    });
  }

  const entries: FaqEntry[] = [];
  for (let i = 0; i < questions.length; i++) {
    const answerEnd = i + 1 < questions.length ? questions[i + 1].questionStart : section.length;
    const answer = clean(section.slice(questions[i].answerStart, answerEnd));
    if (questions[i].text && answer) {
      entries.push({ question: questions[i].text, answer });
    }
  }

  return entries;
};

export const buildFaqPageSchema = (html: string): Record<string, unknown> | null => {
  const entries = extractFaqEntries(html);
  if (entries.length === 0) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: entries.map((entry) => ({
      '@type': 'Question',
      name: entry.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: entry.answer,
      },
    })),
  };
};

export default buildFaqPageSchema;
